import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { minBy } from 'lodash';

import Card from './card';
import EmptyState from 'components/project/empty_state';
import BarChart from '../bar_chart';
import SelectFilter from 'components/search_sidebar/select_filter';
import SearchPage from 'components/search_page';
import { DATE_FORMAT } from 'helpers/formatters/date_time_formats';

import LaptopSVG from 'otter_icons/standalone/laptop.svg';

// Buildout Blue
const WEBSITE_VIEWS_COLOR = '#154A93';

// Electric 2.0
const DOC_DOWNLOADS_COLOR = '#22C3EA';

export const START_DATES = [
  { value: 'default', label: 'Default', pastTense: 'year', momentArgs: [1, 'year'], stepSize: 'auto' },
  { value: 'oneWeek', label: '1 Week', pastTense: '7 days', momentArgs: [7, 'days'], stepSize: 'day' },
  { value: 'oneMonth', label: '1 Month', pastTense: 'month', momentArgs: [1, 'month'], stepSize: 'day' },
  { value: 'threeMonth', label: '3 Months', pastTense: '3 months', momentArgs: [3, 'month'], stepSize: 'week' },
  { value: 'oneYear', label: '1 Year', pastTense: 'year', momentArgs: [1, 'year'], stepSize: 'month' }
];

export default class Website extends React.Component {
  static propTypes = {
    fetchMetrics: PropTypes.func,
    fullScreen: PropTypes.bool,
    metrics: PropTypes.shape({
      documentDownloads: PropTypes.number,
      documentDownloadsByDay: PropTypes.object,
      loadingAnalytics: PropTypes.bool,
      loadingMetrics: PropTypes.bool,
      syndicationPartners: PropTypes.array,
      websiteViews: PropTypes.number,
      websiteViewsByDay: PropTypes.object
    }),
    onSetAsActive: PropTypes.func.isRequired
  }

  state = {
    startDate: null,
    stepSize: 'auto'
  }

  metrics() {
    if (this.state.metrics) return this.state.metrics;

    return this.props.metrics;
  }

  loading() {
    return this.metrics().loadingAnalytics || this.metrics().loadingMetrics || this.metrics().loading;
  }

  handleStartDateChange = ({ momentArgs, stepSize }) => {
    const startDate = moment().subtract(...momentArgs);
    this.setState({ startDate, metrics: { loading: true }, stepSize });

    this.props.fetchMetrics(startDate.format(DATE_FORMAT)).then((metrics) => {
      this.setState({ metrics });
    });
  }

  findMinDateFromDataSets(dataSets) {
    const dateStrings = dataSets.map(dataSet => this.findMinDate(dataSet));
    return minBy(dateStrings, dateString => parseInt(dateString));
  }

  findMinDate(data) {
    if (!data || Object.keys(data).length === 0) return moment();

    const dates = Object.keys(data);

    for (let i = 0; i < dates.length; i++) {
      if (data[dates[i]] !== 0) return dates[i];
    }

    return dates[0];
  }

  calcStepSize(minDate) {
    if (this.state.stepSize !== 'auto') return this.state.stepSize;

    const numMonthsDiff = moment().diff(minDate, 'months');

    if (numMonthsDiff < 1)
      return 'day';
    else if (numMonthsDiff < 3)
      return 'week';
    else
      return 'month';
  }

  timeframeData(data, minDateString) {
    const minDate = moment(minDateString);
    const maxDate = moment();
    const stepSize = this.calcStepSize(minDate);

    const timeframeData = [];

    while (minDate < maxDate && data !== undefined) {
      const x = moment(minDate).startOf(stepSize).format('YYYYMMDD');
      const y = data[minDate.format('YYYYMMDD')] || 0;

      if (timeframeData.length > 0 && timeframeData[timeframeData.length - 1].x === x) {
        timeframeData[timeframeData.length - 1].y += y;
      } else {
        timeframeData.push({ x, y });
      }

      minDate.add(1, 'days');
    }

    return timeframeData;
  }

  graphData = () => {
    const metrics = this.metrics();
    let graphStartDate = this.state.startDate;

    if (this.state.stepSize === 'auto') {
      graphStartDate = this.findMinDateFromDataSets([metrics.websiteViewsByDay, metrics.documentDownloadsByDay]);
    }

    return {
      datasets: [
        {
          label: 'Website Views',
          backgroundColor: WEBSITE_VIEWS_COLOR,
          data: this.timeframeData(metrics.websiteViewsByDay, graphStartDate)
        },
        {
          label: 'Document Downloads',
          backgroundColor: DOC_DOWNLOADS_COLOR,
          data: this.timeframeData(metrics.documentDownloadsByDay, graphStartDate)
        }
      ]
    };
  }

  legendData = () => {
    const metrics = this.metrics();

    return [
      {
        color: WEBSITE_VIEWS_COLOR,
        label: 'Web View',
        count: metrics.websiteViews || 0
      },
      {
        color: DOC_DOWNLOADS_COLOR,
        label: 'Doc Download',
        count: metrics.documentDownloads || 0
      }
    ];
  }

  renderEmptyState() {
    const submessage = 'Traffic and download metrics will be shown here once the website has been published and ' +
      'there is a website view or document download to report.';
    return (
      <EmptyState
        imageSrc={LaptopSVG}
        message="No website metrics to display."
        subMessage={submessage}
      />
    );
  }

  renderBody() {
    const metrics = this.metrics();
    if (metrics.loadingAnalytics) return;
    if (metrics.documentDownloads + metrics.websiteViews === 0) return this.renderEmptyState();

    return (
      <React.Fragment>
        <BarChart graphData={this.graphData()} legendData={this.legendData()} preview={!this.props.fullScreen} />
        {this.props.fullScreen}
      </React.Fragment>
    );
  }

  renderHeader() {
    return (
      <SearchPage>
        <SelectFilter
          defaultValue={{ value: 'default', label: 'Default' }}
          isSearchable={false}
          name="start-date-select"
          options={START_DATES}
          onChange={this.handleStartDateChange}
        />
      </SearchPage>
    );
  }

  render() {
    const { onSetAsActive, metrics, ...passThroughProps } = this.props;

    return (
      <Card
        {...passThroughProps}
        header={this.renderHeader()}
        loading={this.loading()}
        subtitle="Overview of website plugin metrics"
        title="Website Traffic"
        onSetAsActive={(
          this.loading()
          || (metrics.documentDownloads + metrics.websiteViews === 0)
        ) ? null : onSetAsActive}
      >
        {this.renderBody()}
      </Card>
    );
  }
}
